<template>
  <div>
    <v-row>
      <v-col :class="{'pr-0': time}">
        <v-menu ref="menu"
                v-model="showPicker"
                :close-on-content-click="false"
                :z-index="9999"
                max-width="290px"
                min-width="290px"
                offset-y
                transition="scale-transition"
                top
        >
          <template #activator="{ on, attrs }">
            <v-text-field v-model="dateFormatted"
                          :readonly="isMobile"
                          :rules="[rules.required, rules.validDate, rules.isInRanges]"
                          append-icon="icon-calender"
                          filled
                          hint="Format: dd.mm.yyyy"
                          transition="scale-transition"
                          v-bind="attrs"
                          v-on="on"
                          @click:append="showPicker = true"
                          @input="val => userEditedDate(val)"
                          @update:error="err => $emit('error', err)"/>
          </template>

          <v-date-picker v-model="date"
                         :max="parseDateToIsoDate(maxDate)"
                         :min="parseDateToIsoDate(oldestOKDate)"
                         no-title
                         scrollable
                         locale="de"
                         show-adjacent-months>
            <v-spacer/>

            <v-btn color="primary"
                   text
                   @click="showPicker = false">
              Abbrechen
            </v-btn>
            <v-btn :color="$finColors.primaryDark"
                   dark
                   @click="saveDate">
              Übernehmen
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col v-if="timePicker"
             :class="{'pl-0': time}"
             cols="5">
        <fin-time-picker v-model="time"
                         :maxTime="newestOKTime"
                         :minTime="oldestOKTime"
                         @input="val => this.time = val"
                         @error="err => $emit('error', err)"/>
      </v-col>
    </v-row>

    <div v-if="debug">
      <pre>Debug Info</pre>
      <pre v-if="value">value: {{ this.value }} </pre>
      <pre v-if="date">date: {{ date }}</pre>
      <pre v-if="dateFormatted">dateFormatted: {{ dateFormatted }}</pre>
      <pre v-if="minDate">minDate: {{ minDate }} </pre>
      <pre v-if="oldestOKDate">oldestOkDate: {{ oldestOKDate }} </pre>
      <pre v-if="time">time: {{ time }} </pre>
      <pre v-if="dateFormatted && time">new Date based on date and time: {{
          new Date(`${this.date}T${this.time}`)
        }} </pre>
    </div>
  </div>
</template>

<script>
import finBrowser from "@/js/finBrowser"
import FinTimePicker from "@/components/common/TimePicker"

export default {
  name: "FinDatePicker",
  components: {FinTimePicker},
  data() {
    return {
      isMobile: finBrowser.IS_MOBILE(),
      showPicker: false,
      date: this.value ? this.value.toISOString().substr(0, 10) : null,
      dateFormatted: undefined,
      // there is no older timeseries data available
      defaultMinDate: new Date("2002-09-30"),
      rules: {
        required: value => !!value || 'Benötigt',
        validDate: value => {
          return this.validateDate(value) || 'Ungültiges Datum'
        },
        isInRanges: value => {
          return this.isDateInRange(value) || `Das Datum muss zwischen ${this.formatDate((this.parseDateToIsoDate(this.oldestOKDate)))} und heute sein`
        }
      }
    }
  },
  props: {
    /**
     * Displays debug date information
     */
    debug: {
      type: Boolean,
      default: false
    },
    /** The selected date */
    value: {
      type: Date,
      default() {
        return new Date()
      }
    },
    /**
     * Date in date ISO-string format YYYY-MM-TT. Since the oldest instrument's data available
     * in the application is maximal 20 years old at the go-live-date (30.09.2021), the date
     * is set to 20 years before that. Therefore 30.09.2002.
     */
    minDate: {
      type: Date,
      default: function () {
        return new Date("2002-09-30")
      }
    },
    /**
     * The newest date that can be used is the now-date.
     */
    maxDate: {
      type: Date,
      default: function () {
        return new Date()
      }
    },
    /**
     * Show timepicker
     */
    timePicker: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    /**
     * oldestOKDate returns the smallest between minDate and default fallback defaultMinDate.
     * oldestOKDate is used to validate the date the user chooses
     */
    oldestOKDate() {
      return this.minDate < this.defaultMinDate ? this.defaultMinDate : this.minDate
    },
    oldestOKTime() {
      let date = this.oldestOKDate
      if (this.date === this.parseDateToIsoDate(date)) {
        return ("0" + date.getHours()).slice(-2) + ":" + ("0" + date.getMinutes()).slice(-2)
      }
      return undefined
    },
    newestOKTime() {
      let date = this.maxDate
      if (this.date === this.parseDateToIsoDate(date)) {
        return ("0" + date.getHours()).slice(-2) + ":" + ("0" + date.getMinutes()).slice(-2)
      }
      return undefined
    },
    time: {
      get() {
        if (!this.value) return undefined
        try {
          // Workaround for single digits
          let res = "00:00"
          if (this.value) {
            res = ("0" + this.value.getHours()).slice(-2) + ":" + ("0" + this.value.getMinutes()).slice(-2)
          }
          return res
        } catch (e) {
          return undefined
        }
      },
      set(newVal) {
        // without a blank instead of T the date would not be ISO valid and would not be accepted on iOS
        let result = new Date(`${this.date}T${newVal}`)
        this.$emit('input', result)
      }
    },
  },
  methods: {
    userEditedDate(val) {
      if (this.validateDate(val)) {
        this.date = this.parseDate(val)
        this.saveDate()
      }
    },
    /**
     * Format given date
     *
     * @param date Date string in format (yyyy-mm-dd)
     * @return {string|null} Formatted Date as string
     */
    formatDate(date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      return `${day}.${month}.${year}`
    },
    /**
     * @param date Date string in format (dd.mm.yyyy)
     */
    parseDate(date) {
      if (!date) return null
      const [day, month, year] = date.split('.')

      let parsedDate = undefined
      if (day && month && year) {
        parsedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      }
      return parsedDate
    },
    /**
     * @param val Date in JS-date-format
     */
    parseDateToIsoDate(val) {
      if (!val) return null
      return `${val.getFullYear()}-${(val.getUTCMonth() + 1).toString().padStart(2, '0')}-${("0" + val.getDate()).slice(-2)}`
    },
    /**
     * save date
     */
    saveDate() {
      if ((
          this.timePicker &&
          !isNaN(new Date(`${this.date}T${this.time}`).getTime())
        ) ||
        (
          !this.timePicker &&
          !isNaN(new Date(`${this.date}`).getTime()))
      ) {
        this.$refs.menu.save(this.date)
        this.dateFormatted = this.formatDate(this.date)

        this.$emit('input', new Date(`${this.date}T${this.time}`))
      }
    },
    /**
     * Validates given date
     * @param val date in format (dd.mm.yyyy)
     * @return {boolean} True if date is valid, false if not
     */
    validateDate(val) {
      try {
        if (val !== undefined) {
          val = this.parseDate(val)
          val = new Date(val)
          return !isNaN(val.getTime())
        } else return false
      } catch (e) {
        console.error("DatePicker validateDate catch error", val, e)
        return false
      }
    },
    /**
     * Checks if given date is in range (not newer than today)
     * @param val date in format (dd.mm.yyyy)
     * @return {boolean} True if date is in range (oldestOKDate to today), false if not
     */
    isDateInRange(val) {
      try {
        if (val !== undefined) {
          val = this.parseDate(val)
          let timeStamp = new Date(val).getTime()
          let from = new Date(this.oldestOKDate).getTime()
          let to = new Date().getTime()

          // If currentDate == oldestOkDate -> Check Time instead
          if (this.oldestOKTime) {
            return true
          }
          return timeStamp >= from && timeStamp <= to
        } else return false
      } catch (e) {
        console.error("DatePicker isDateInRange catch error", val, e)
        return false
      }
    },
  },
  beforeMount() {
    let date = ""
    if (this.value) {
      date = (new Date(this.value - (this.value).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)
    } else {
      date = (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)
    }
    this.dateFormatted = this.formatDate(date)
  },
  watch: {
    value(val) {
      //let date = val.toISOString().substr(0,10)
      let date = (new Date(val - (val).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)
      this.dateFormatted = this.formatDate(date)
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
